// Copied from coulomb-commons-ui soh-band-migration 0.1.0 (buildSankeyGraph / nodeId in
// src/shaping/alluvialShaping.js).
//
// Change from the original: the input is already aggregated -- a count per
// (column, bucket) node and a count per ribbon between adjacent columns -- instead
// of per-entity bucket paths. Node size is the node count; ribbons are drawn from
// the flow list. Nothing here knows about any API.

import { computeSankeyGeometry } from "./sankeyGeometry";

/** Node id convention: column index + bucket index. Also the hover/click key. */
export const nodeId = (columnIndex, bucketIndex) => `n-${columnIndex}-${bucketIndex}`;

/** A count from the input, or 0 when it is missing / not a number / negative. */
export function toCount(value) {
  const n = parseFloat(value);
  return !isNaN(n) && n > 0 ? n : 0;
}

/**
 * Builds the { nodes, edges } graph d3-sankey lays out, plus the lookup tables the
 * chart uses to interpret events and draw ribbons.
 *
 *   nodes   one per (column, bucket) with count > 0, id nodeId(c, b), coloured by
 *           its bucket, y0/y1 pre-computed so buckets keep legend order.
 *   edges   one per (column c -> c + 1, source bucket, target bucket) with count > 0.
 *           A flow that is not between adjacent columns, names an unknown column or
 *           bucket, or has an endpoint with no node, is dropped (d3-sankey throws on
 *           a link to a missing node).
 *
 * @param {Object} args
 * @param {{key:string,label:string}[]} args.columns   in display (time) order
 * @param {{id:string,label:string,color:string}[]} args.buckets  in display (top -> bottom) order
 * @param {{columnKey:string,bucketId:string,count:number}[]} args.nodes
 * @param {{fromColumnKey:string,toColumnKey:string,fromBucketId:string,toBucketId:string,count:number}[]} args.flows
 */
export function buildSankeyGraph({ columns = [], buckets = [], nodes = [], flows = [] } = {}) {
  const safeColumns = Array.isArray(columns) ? columns : [];
  const safeBuckets = Array.isArray(buckets) ? buckets : [];
  const bucketCount = safeBuckets.length;
  const columnIndexByKey = new Map(safeColumns.map((c, i) => [c.key, i]));
  const bucketIndexById = new Map(safeBuckets.map((b, i) => [b.id, i]));

  const distributions = safeColumns.map(() => new Array(bucketCount).fill(0));
  (Array.isArray(nodes) ? nodes : []).forEach((n) => {
    const c = columnIndexByKey.get(n?.columnKey);
    const b = bucketIndexById.get(n?.bucketId);
    if (c === undefined || b === undefined) return;
    distributions[c][b] += toCount(n.count);
  });

  const flowCounts = safeColumns.slice(1).map(() => new Map()); // key `${s}-${t}`
  (Array.isArray(flows) ? flows : []).forEach((f) => {
    const from = columnIndexByKey.get(f?.fromColumnKey);
    const to = columnIndexByKey.get(f?.toColumnKey);
    const s = bucketIndexById.get(f?.fromBucketId);
    const t = bucketIndexById.get(f?.toBucketId);
    const value = toCount(f?.count);
    if (from === undefined || to !== from + 1) return;
    if (s === undefined || t === undefined || value <= 0) return;
    if (distributions[from][s] <= 0 || distributions[to][t] <= 0) return;
    const key = `${s}-${t}`;
    flowCounts[from].set(key, (flowCounts[from].get(key) ?? 0) + value);
  });

  const total = Math.max(0, ...distributions.map((d) => d.reduce((a, b) => a + b, 0)));

  const edges = [];
  const edgeMeta = new Map();
  flowCounts.forEach((counts, p) => {
    Array.from(counts.entries())
      .map(([key, value]) => ({ value, s: Number(key.split("-")[0]), t: Number(key.split("-")[1]) }))
      .sort((a, b) => a.s - b.s || a.t - b.t)
      .forEach(({ value, s, t }) => {
        const source = nodeId(p, s);
        const target = nodeId(p + 1, t);
        edges.push({ source, target, value });
        edgeMeta.set(`${source}>${target}`, {
          id: `${source}>${target}`,
          source,
          target,
          fromIndex: p,
          fromBucket: s,
          toBucket: t,
          value,
        });
      });
  });

  // Node slots and ribbon ends, with the minimum node / ribbon sizes applied.
  const { absY, ribbons } = computeSankeyGeometry(distributions, Array.from(edgeMeta.values()));

  const graphNodes = [];
  const nodeMeta = new Map();
  distributions.forEach((dist, c) => {
    dist.forEach((count, b) => {
      if (count <= 0) return;
      const bucket = safeBuckets[b];
      const id = nodeId(c, b);
      graphNodes.push({
        id,
        title: `${bucket.label} · ${safeColumns[c].label}`,
        color: bucket.color,
        fixedValue: count,
        y0: absY[c][b].y0,
        y1: absY[c][b].y1,
      });
      nodeMeta.set(id, {
        id,
        columnIndex: c,
        columnKey: safeColumns[c].key,
        bucketIndex: b,
        bucketId: bucket.id,
        count,
      });
    });
  });

  return { nodes: graphNodes, edges, nodeMeta, edgeMeta, distributions, total, absY, ribbons };
}
